import { useState } from "react";
import classNames from "classnames";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import Dropdown from "~/components/shared/Dropdown";
import Icon from "~/components/shared/Icon";
import { CancelButton } from "~/components/build/Header/CancelButton";
import RetryFailedJobsButton from "~/components/build/Header/RetryFailedJobsButton";

export function CollapsedBuildActions() {
  const { build, store } = useBuild();

  return (
    <>
      <CancelButton
        build={build}
        store={store}
        // Tidy this up when we've got a single build experience
        className="btn-small rounded-sm border border-slate-400 font-medium !text-red-500 shadow-sm transition-all hover:border-slate-500 hover:bg-gray-100 hover:shadow-sm active:bg-gray-300 active:shadow-inner"
      />
      <BuildActions />
    </>
  );
}

/**
 * Build actions component
 * Renders either a single action as a button or a combo-button/dropdown depending on
 * various build attributes and user permissions
 * @returns JSX.Element
 */
function BuildActions() {
  const { build, store } = useBuild();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const showRetryFailures =
    build.permissions.retry?.allowed &&
    (build.canRetryFailedJobs || build.canRetryFailedTriggeredJobs);

  const showRebuildOptions =
    !build.project.archived && (build.state === "canceling" || build.finishedAt);

  const showFullRebuildOptions =
    showRebuildOptions && build.permissions.rebuild?.allowed && build.project.allowRebuilds;

  const showDisabledRebuildOption =
    showRebuildOptions &&
    !showFullRebuildOptions &&
    build.permissions.rebuild?.reason !== "anonymous";

  const showDisabledForLoggedOut = build.permissions.rebuild?.reason === "anonymous";

  if (!showRetryFailures && !showRebuildOptions) {
    return null;
  }

  if (showRetryFailures && !showRebuildOptions) {
    return (
      <RetryFailedJobsButton
        build={build}
        store={store}
        className="btn-small flex-shrink-0 whitespace-nowrap rounded-sm border border-slate-400 font-medium shadow-sm transition-all hover:border-slate-500 hover:bg-gray-100 active:bg-gray-300 active:shadow-inner"
        compactMode={true}
      />
    );
  }

  if (!showRetryFailures && showDisabledRebuildOption) {
    return (
      <span
        className="btn btn-small btn-flex btn-default build-rebuild-button btn--disabled text-center"
        title={build.permissions.rebuild?.message}
      >
        <Icon
          icon="custom/outline/16x/arrows-refresh"
          style={{ width: 16, height: 16 }}
          className="mr-1"
        />
        <span>Rebuild</span>
      </span>
    );
  }

  if (showDisabledForLoggedOut) {
    return (
      <button
        className="btn btn-small btn-flex btn-default build-rebuild-button btn--disabled text-center"
        title={build.permissions.rebuild?.message}
        disabled={true}
      >
        <Icon
          icon="custom/outline/16x/arrows-refresh"
          style={{ width: 16, height: 16 }}
          className="mr-1"
        />
        <span>New build</span>
      </button>
    );
  }

  // When there are failures to retry we'll make that the default option, otherwise we'll
  // default to new build
  const defaultAction = showRetryFailures ? (
    <RetryFailedJobsButton
      build={build}
      store={store}
      className="btn-small rounded-sm rounded-r-none border border-slate-400 font-medium shadow-sm transition-all hover:border-slate-500 hover:bg-gray-100 hover:text-inherit focus:text-inherit active:bg-gray-300 active:shadow-inner"
      compactMode={true}
      disableTooltip={dropdownOpen}
    />
  ) : (
    <a
      href={build.newPath}
      className="btn btn-small build-rebuild-button btn-flex rounded-sm rounded-r-none border border-slate-400 text-center font-medium shadow-sm transition-all hover:border-slate-500 hover:bg-gray-100 hover:text-inherit focus:text-inherit active:bg-gray-300 active:shadow-inner"
      data-turbo={true}
      data-turbo-frame="modal_dialog"
    >
      <Icon icon="heroicons/outline/plus" style={{ width: 14, height: 14 }} className="mr-1" />
      <span>New build</span>
    </a>
  );

  // Only show the New build option in dropdown when it's not the default action
  const showNewBuildInDropdown = showRetryFailures;

  return (
    <div className="flex shrink-0">
      {defaultAction}
      <Dropdown width={200} className="inline-block" onToggle={setDropdownOpen}>
        <button
          className="btn-small btn btn-flex cursor-pointer select-none rounded-sm rounded-l-none border border-l-0 border-slate-400 font-medium shadow-sm transition-all hover:border-slate-500 hover:bg-gray-100 active:bg-gray-300 active:shadow-inner"
          title="Rebuild options"
        >
          <Icon icon="down-triangle" style={{ width: 7, height: 7 }} />
        </button>
        {showFullRebuildOptions ? (
          <>
            <DropdownItem href={build.rebuildPath} data-method="post">
              <Icon icon="custom/outline/16x/arrows-refresh" style={{ width: 16, height: 16 }} />
              <span>Rebuild</span>
            </DropdownItem>
            <DropdownItem href={build.rebuildBranchPath} data-method="post">
              <Icon icon="custom/outline/git-branch" style={{ width: 16, height: 16 }} />
              <span>Rebuild this branch</span>
            </DropdownItem>
            {showNewBuildInDropdown && (
              <>
                <div className="my-1 h-px w-full bg-gray-500" />
                <DropdownItem
                  href={build.newPath}
                  data-turbo={true}
                  data-turbo-frame="modal_dialog"
                >
                  <Icon icon="heroicons/outline/plus" style={{ width: 14, height: 14 }} />
                  <span>New build</span>
                </DropdownItem>
              </>
            )}
          </>
        ) : null}
        {showDisabledRebuildOption ? (
          <DropdownItem disabled={true}>
            <span className="text-center" title={build.permissions.rebuild.message}>
              <Icon
                icon="custom/outline/16x/arrows-refresh"
                style={{ width: 16, height: 16 }}
                className="mr-1"
              />
              <span>Rebuild</span>
            </span>
          </DropdownItem>
        ) : null}
      </Dropdown>
    </div>
  );
}

function DropdownItem({
  children,
  disabled = false,
  ...props
}: React.HTMLProps<HTMLAnchorElement>) {
  const preventClick = (event) => {
    event.preventDefault();
  };
  return (
    <a
      {...props}
      onClick={disabled ? preventClick : undefined}
      rel="nofollow"
      className={classNames(
        "mx-1.5 flex items-center gap-1 rounded-md px-2 py-2 font-medium text-inherit hover:no-underline",
        {
          "hover:bg-purple-100 hover:text-purple-600": !disabled,
          "cursor-not-allowed opacity-50 hover:text-inherit": disabled,
        },
      )}
    >
      {children}
    </a>
  );
}
